import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ls from 'local-storage';
import BuyTokensModal from "modals/BuyTokensModal";
import moment from 'moment';
import { useDispatch, useMappedState } from 'redux-react-hook';
import { GUEST_ROUTES } from 'routes/routes';
import { updateUser } from 'store/DEPRECATEDusers/asyncAction';
import styled from 'styled-components';
import { errorAlert } from 'ui-kit/Alert';
import Spinner from 'ui-kit/Spinner';
import { isBlank } from 'utils/storeUtils';

import TrackingAPI from '../../../api/TrackingAPI';
import {
  useCreateWizardGameMutation,
  useLazyGetPrivateGamesQuery,
  useLazyGetPublicGamesQuery
} from '../../../services/api/gamesApi';
import {
  useGenerateAppearanceMutation,
  useGenerateTitleMutation,
  useIsLegalMutation,
  useRequestAiNamesMutation,
  useRequestCharactersMutation,
  useRequestInitialMessagesMutation,
  useRequestLocationMutation,
  useRequestScenarioMutation,
  useRequestSetupMutation
} from '../../../services/api/generateScenarioApi';
import { getAllTags } from '../../games/tools/PublicGamesListTool';
import NiceConfirm from '../../modals/NiceConfirm';
import NiceModal from '../../modals/NiceModal';
import SubscriptionModal from '../../modals/SubscriptionModal';
import { CharacterProfiles } from './CharacterProfiles';
import { CharactersNamesTool } from './CharactersNamesTool';
import { ConfirmScenarioTool } from './ConfirmScenarioTool';
import { InitialMessagesTool } from './InitialMessagesTool';
import { ScenarioLocationTool } from './ScenarioLocationTool';
import { ScenarioSetupTool } from './ScenarioSetupTool';
import { ScriptIdeaTool } from './ScriptIdeaTool';

export const MAX_PICTURES_PER_SCENARIO = 5;
export const MAX_PICTURES_PER_SCENARIO_PREMIUM = 10;
export const MAX_SCENARIOS_PER_DAY = 10;

export const validateName = name => {
  if (isBlank(name) || name?.trim()?.length === 0) {
    return false;
  }
  const validPattern = /^[a-zA-Z\s.]+$/;

  return validPattern.test(name);
};

/**
 * @deprecated This component is deprecated and will be removed in future versions.
 * Please use ScenariosGeneratorPage component instead.
 */
export const GenerateScenarioTool = (props) => {
  const { initialTags = [], onClose = () => { } } = props;

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        hasSubscription: hasSubscription
      };
    }, [])
  );

  const [currentStep, setCurrentStep] = useState(0);

  const [gamesCreated, setGamesCreated] = useState([]);

  const [playerName, setPlayerName] = useState(currentUser?.characterName || '');
  const [charactersNames, setCharactersNames] = useState([]);
  const [scenarioIdea, setScenarioIdea] = useState('');
  const [charactersDescription, setCharactersDescription] = useState({});
  const [playerDescription, setPlayerDescription] = useState(
    currentUser?.characterDescription || ''
  );
  const [charactersAvatars, setCharactersAvatars] = useState({});
  const [charactersAppearance, setCharactersAppearance] = useState({});
  const [scenarioName, setScenarioName] = useState('');
  const [setup, setSetup] = useState('');
  const [location, setLocation] = useState('');
  const [initialMessages, setInitialMessages] = useState([]);
  const [selectedTags, setSelectedTags] = useState(initialTags || []);
  const [picturesGenerated, setPicturesGenerated] = useState(0);

  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingCreateScenario, setIsLoadingCreateScenario] = useState(false);

  const [backConfirmModal, setBackConfirmModal] = useState(undefined);

  const [subscriptionModalState, setSubscriptionModalState] = useState({
    isOpen: false,
    renderedForReason: undefined
  });
  const [buyTokensModalOpen, setBuyTokensModalOpen] = useState(false);
  const [showContinueModal, setShowContinueModal] = useState(undefined);

  const [generateTitle] = useGenerateTitleMutation();
  const [requestAiNames] = useRequestAiNamesMutation();
  const [requestCharacters] = useRequestCharactersMutation();

  const [generateAppearance] = useGenerateAppearanceMutation();
  const [requestSetup] = useRequestSetupMutation();
  const [requestLocation] = useRequestLocationMutation();
  const [requestInitialMessages] = useRequestInitialMessagesMutation();
  const [requestScenario] = useRequestScenarioMutation();
  const [isLegalRequest] = useIsLegalMutation();

  const [getPublicGames] = useLazyGetPublicGamesQuery();
  const [getPrivateGames] = useLazyGetPrivateGamesQuery();
  const [createWizardGame] = useCreateWizardGameMutation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const saveDataToStorage = (step, last = false) => {
    const data = {};
    if (step > 0) {
      data.playerName = playerName;
      data.scenarioIdea = scenarioIdea;
      data.selectedTags = selectedTags;
    }
    if (step > 1) {
      data.charactersNames = charactersNames;
    }
    if (step > 2) {
      data.playerDescription = playerDescription;
      data.charactersDescription = charactersDescription;
      data.charactersAvatars = charactersAvatars;
      data.charactersAppearance = charactersAppearance;
    }
    if (step > 3) {
      data.setup = setup;
    }
    if (step > 4) {
      data.location = location;
    }
    if (step > 5) {
      data.initialMessages = initialMessages;
    }
    if (step > 6) {
      data.scenarioName = scenarioName;
    }
    if (last) {
      data.scenarioName = scenarioName;
    }

    ls.set('scenario_wizard_data', data);
    ls.set('scenario_wizard_step', step);
  };

  const setDataFromStorage = () => {
    const data = ls.get('scenario_wizard_data');
    const step = ls.get('scenario_wizard_step');

    if (step > 0) {
      setPlayerName(data?.playerName);
      setScenarioIdea(data?.scenarioIdea);
      setSelectedTags(data?.selectedTags);
    }
    if (step > 1) {
      setCharactersNames(data?.charactersNames);
    }
    if (step > 2) {
      setPlayerDescription(data?.playerDescription);
      setCharactersDescription(data?.charactersDescription);
      setCharactersAvatars(data?.charactersAvatars);
      setCharactersAppearance(data?.charactersAppearance);
    }
    if (step > 3) {
      setSetup(data?.setup);
    }
    if (step > 4) {
      setLocation(data?.location);
    }
    if (step > 5) {
      setInitialMessages(data?.initialMessages);
    }
    if (step > 6) {
      setScenarioName(data?.scenarioName);
    }
  };

  const onBack = (callback = () => { }) => {
    setBackConfirmModal({ action: callback });
  };

  const onContinue = () => {
    setCurrentStep(currentStep + 1);
  };

  const checkIfLegal = async data => {
    const { data: isLegal } = await isLegalRequest(data);
    if (isLegal === false) {
      errorAlert('Restricted content. Terms of use violation. Please try different text.');
    }
    return isLegal;
  };

  useEffect(() => {
    if (currentStep > 1) {
      saveDataToStorage(currentStep);
    }
  }, [currentStep]);

  useEffect(() => {
    const wizardData = ls.get('scenario_wizard_data');
    const wizardStep = ls.get('scenario_wizard_step');
    if (wizardData && wizardStep) {
      setShowContinueModal({ data: wizardData, step: wizardStep });
    } else {
      setCurrentStep(1);
    }
    setLoading(true);

    getPublicGames()
      .then(({ data: publicGames }) => {
        const tags = Array.from(getAllTags(publicGames)?.keys());
        setAllTags(tags);
        getPrivateGames()
          .then(({ data: privateGames }) => {
            setGamesCreated(privateGames || []);
            setLoading(false);
          })
          .catch(err => {
            setLoading(false);
            throw err;
          });
      })
      .catch(err => {
        setLoading(false);
        throw err;
      });
  }, []);

  if (loading) {
    return (
      <NiceModal
        onClose={() => {
          onClose();
        }}
      >
        <OuterWrapper>
          <Wrapper>
            <div className="flex h-16 w-[min(700px,calc(100vw-32px))] items-center justify-center">
              <Spinner />
            </div>
          </Wrapper>
        </OuterWrapper>
      </NiceModal>
    );
  }

  return (
    <NiceModal
      onClose={() => {
        saveDataToStorage(currentStep);
        onClose();
      }}
      closeOnEsc={false}
    >
      <OuterWrapper>
        <Wrapper>
          <Header>Step {currentStep}/7</Header>

          {currentStep === 1 && (
            <ScriptIdeaTool
              initialTags={selectedTags}
              initialScenarioIdea={scenarioIdea}
              initialPlayerName={playerName}
              currentUser={currentUser}
              allTags={allTags}
              onChange={(name, idea, tags) => {
                setPlayerName(name);
                setScenarioIdea(idea);
                setSelectedTags(tags);
              }}
              onContinue={async (name, idea, tags) => {
                const [isLegal, { data: aiNames }] = await Promise.all([
                  checkIfLegal({ backstory: idea }),
                  requestAiNames({ scenarioIdea: idea, playerName: name }),
                ]);

                if (!isLegal) {
                  return;
                }

                setPlayerName(name);
                setScenarioIdea(idea);
                setSelectedTags(tags);

                if (aiNames && aiNames['ai-names']) {
                  setCharactersNames(aiNames['ai-names']);
                }
                TrackingAPI.trackEvent('custom_scenario_idea');
                onContinue();
              }}
            />
          )}
          {currentStep === 2 && (
            <CharactersNamesTool
              initialPlayerName={playerName}
              initialCharactersNames={charactersNames}
              onChange={characters => {
                setCharactersNames(characters);
              }}
              onBack={() => {
                onBack(() => {
                  setCharactersNames([]);
                });
              }}
              onContinue={async characters => {
                setCharactersNames(characters);
                const { data: charactersInfo } = await requestCharacters({
                  scenarioIdea,
                  playerName,
                  aiNames: characters
                });
                const charactersDescription = charactersInfo?.characters;
                setCharactersDescription(charactersDescription);

                const avatars = {};
                const appearance = {};

                await Promise.allSettled(
                  Object.keys(charactersDescription)?.map(name =>
                    generateAppearance({ text: charactersDescription[name], name })
                  )
                ).then(results => {
                  results.forEach(res => {
                    if (res.status === 'fulfilled' && res.value) {
                      avatars[res.value.data.name] = 'gap';
                      appearance[res.value.data.name] = res.value.data.appearance;
                    }
                  });
                });

                setCharactersAvatars(avatars);
                setCharactersAppearance(appearance);
                TrackingAPI.trackEvent('custom_scenario_characters_names');
                onContinue();
              }}
            />
          )}
          {currentStep === 3 && (
            <CharacterProfiles
              hasSubscription={hasSubscription}
              initialPlayerDescription={playerDescription}
              initialCharactersDescription={charactersDescription}
              initialCharactersAvatars={charactersAvatars}
              initialCharactersAppearance={charactersAppearance}

              onChange={(player, characters, avatars, appearance) => {
                setPlayerDescription(player);
                setCharactersDescription(characters);
                setCharactersAvatars(avatars);
                setCharactersAppearance(appearance);
              }}
              onBack={() => {
                onBack(() => {
                  setCharactersDescription({});
                  setCharactersAvatars({});
                  setCharactersAppearance({});
                });
              }}
              onGenerate={() => {
                setPicturesGenerated(picturesGenerated + 1);
                ls.set('scenario_wizard_pictures_generated', picturesGenerated + 1);
              }}
              onContinue={async (player, characters, avatars, appearance) => {
                const [{ data: initialSetup }, playerLegal, ...charactersLegal] = await Promise.all([
                  requestSetup({
                    scenarioIdea,
                    playerName,
                    aiNames: charactersNames,
                    characters
                  }),
                  isLegalRequest({ backstory: player }),
                  ...Object.keys(characters).map(name =>
                    isLegalRequest({ backstory: characters[name] })
                  )
                ]);

                if (!playerLegal || charactersLegal.includes(false)) {
                  errorAlert(
                    'Restricted content. Terms of use violation. Please try different description.'
                  );
                  return;
                }

                TrackingAPI.trackEvent('custom_scenario_character_description');
                setPlayerDescription(player);
                setCharactersDescription(characters);
                setCharactersAvatars(avatars);
                setCharactersAppearance(appearance);
                setSetup(initialSetup);
                onContinue();
              }}
              showPremiumModal={() => {
                setSubscriptionModalState({
                  isOpen: true,
                  renderedForReason: 'generate_scenario_step_3'
                });
              }}
              onOpenBuyTokens={() => setBuyTokensModalOpen(true)}
            />
          )}

          {currentStep === 4 && (
            <ScenarioSetupTool
              initialSetup={setup}
              onBack={async () => {
                onBack(() => {
                  setSetup('');
                });
              }}
              onChange={scriptSetup => {
                setSetup(scriptSetup);
              }}
              onContinue={async scriptSetup => {
                TrackingAPI.trackEvent('custom_scenario_setup');
                setSetup(scriptSetup);

                const { data: initialLocation } = await requestLocation({
                  scenarioIdea,
                  playerName,
                  setup: scriptSetup,
                  characters: charactersDescription
                });
                setLocation(initialLocation);
                onContinue();
              }}
            />
          )}

          {currentStep === 5 && (
            <ScenarioLocationTool
              initialLocation={location}
              onChange={scriptLocation => {
                setLocation(scriptLocation);
              }}
              onBack={() => {
                onBack(() => {
                  setLocation('');
                });
              }}
              onContinue={async scriptLocation => {
                TrackingAPI.trackEvent('custom_scenario_location');
                setLocation(scriptLocation);
                const { data: messages } = await requestInitialMessages({
                  scenarioIdea,
                  playerName,
                  aiNames: charactersNames,
                  setup,
                  characters: charactersDescription,
                  location: scriptLocation
                });
                setInitialMessages(messages);
                onContinue();
              }}
            />
          )}

          {currentStep === 6 && (
            <InitialMessagesTool
              messages={initialMessages}
              charactersAvatars={charactersAvatars}
              charactersNames={charactersNames}
              playerName={playerName}
              onChange={messages => {
                setInitialMessages(messages);
              }}
              onBack={() => {
                onBack(() => {
                  setInitialMessages([]);
                });
              }}
              onContinue={async messages => {
                const [{ data: generatedTitle }, ...legalResults] = await Promise.all([
                  generateTitle({
                    scenarioIdea,
                    playerName,
                    charactersNames,
                    setup
                  }),
                  ...messages.map(x => isLegalRequest({ backstory: x?.text }))
                ]);

                if (legalResults.includes(false)) {
                  errorAlert(
                    'Restricted content. Terms of use violation. Please try different messages.'
                  );
                  return;
                }

                TrackingAPI.trackEvent('custom_scenario_initial_messages');
                setInitialMessages(messages);

                let name = generatedTitle?.replace(/(\\)|(\")/g, '');
                if (name.startsWith('Title:')) {
                  name = name.replace('Title:', '');
                }
                setScenarioName(name?.trim());

                onContinue();
              }}
            />
          )}

          {currentStep === 7 && (
            <ConfirmScenarioTool
              initialScenarioName={scenarioName}
              scenarioIdea={scenarioIdea}
              playerName={playerName}
              playerDescription={playerDescription}
              charactersNames={charactersNames}
              charactersDescription={charactersDescription}
              charactersAvatars={charactersAvatars}
              isLoadingCreateScenario={isLoadingCreateScenario}
              setup={setup}
              location={location}
              onChange={scriptName => {
                setScenarioName(scriptName);
              }}
              onBack={() => {
                onBack(() => {
                  setScenarioName('');
                });
              }}
              onStart={async scriptName => {
                TrackingAPI.trackEvent('custom_scenario_start_generate');

                const createdToday = gamesCreated?.filter(
                  t => moment(t?.timestamp).unix() >= moment().startOf('day').unix()
                );
                if (createdToday.length >= MAX_SCENARIOS_PER_DAY) {
                  errorAlert('You have reached the daily limit of 10 scenarios');
                  TrackingAPI.trackEvent('custom_scenario_finish_daily_limit');
                  return;
                }

                const avatars = {};
                for (const key in charactersAvatars) {
                  avatars[key.toLowerCase()] = { default: charactersAvatars[key] };
                }

                const newUserData = {};
                let shouldUpdateUser = false;
                if (currentUser?.characterName != playerName) {
                  newUserData.characterName = playerName;
                  shouldUpdateUser = true;
                }
                if (
                  currentUser?.characterDescription == undefined ||
                  currentUser?.characterDescription == ''
                ) {
                  if (playerDescription != '') {
                    newUserData.characterDescription = playerDescription;
                    shouldUpdateUser = true;
                  }
                }

                if (shouldUpdateUser) {
                  await dispatch(updateUser({ ...newUserData, id: currentUser?.id }));
                }

                setIsLoadingCreateScenario(true);

                const { data: scenarioConfig } = await requestScenario({
                  scenarioIdea,
                  playerName,
                  aiNames: charactersNames,
                  setup,
                  characters: charactersDescription,
                  location,
                  initialMessages: initialMessages,
                  avatars: avatars,
                  appearance: charactersAppearance,
                  config: {
                    tags: selectedTags || []
                  }
                });
                if (scenarioConfig == undefined) {
                  errorAlert('Something went wrong, try again later');
                  TrackingAPI.trackEvent('custom_scenario_finish_error');
                  return;
                }

                const aiNames = charactersNames?.map(x => x?.toLowerCase());

                try {
                  scenarioConfig.initial_messages = scenarioConfig.initial_messages?.map(x =>
                    x?.name == 'Narrator' ? { role: 'assistant', text: x?.text } : x
                  );
                  scenarioConfig.initial_messages = scenarioConfig.initial_messages?.map(x =>
                    x?.name == playerName ? { ...x, extra: { isUser: true } } : x
                  );
                  scenarioConfig.system_message_parts = {
                    ...scenarioConfig.system_message_parts,
                    characters_description: {
                      ...charactersDescription,
                      '{{user}}': playerDescription
                    }
                  };
                  scenarioConfig.ai_names = aiNames;
                  scenarioConfig.name = scriptName;
                  scenarioConfig.scenario_name = scriptName;
                  scenarioConfig.post_message =
                    'USER: Please generate a message that contains only one idea that is expressed in only two speech and maybe two actions, but not more. ';
                } catch (_error) { }
                const creationData = {
                  userId: currentUser.id,
                  name: scriptName,
                  description: scenarioIdea,
                  publicName: scriptName,
                  publicDescription: scenarioIdea
                };
                let pld;
                try {
                  const { data } = await createWizardGame({ data: creationData, configJson: scenarioConfig });
                  pld = data;
                } catch (_exc) {
                  errorAlert('Something went wrong, try again later');
                  TrackingAPI.trackEvent('custom_scenario_wizard_finish_error');
                  return;
                }
                if (pld != undefined) {
                  const { game } = pld;
                  TrackingAPI.trackEvent('custom_scenario_finish');
                  ls.remove('scenario_wizard_data');
                  ls.remove('scenario_wizard_step');
                  navigate(GUEST_ROUTES.ROLEPLAY(game.alias));
                }
              }}
            />
          )}

          {backConfirmModal == undefined ? null : (
            <NiceConfirm
              cancelText={'Cancel'}
              confirmText={'Ok'}
              heading={'Back'}
              subHeading={
                'Are you sure you want to go back? You will lose the data you filled in at this step'
              }
              onCancel={() => {
                setBackConfirmModal(undefined);
              }}
              onConfirm={() => {
                setCurrentStep(currentStep - 1);
                backConfirmModal.action();
                setBackConfirmModal(undefined);
              }}
            />
          )}
          {subscriptionModalState.isOpen && (
            <SubscriptionModal
              analyticsData={{
                rendered_for_reason: subscriptionModalState.renderedForReason
              }}
              isOpen
              onClose={() => {
                setSubscriptionModalState({
                  isOpen: false,
                  renderedForReason: undefined
                });
              }}
            />
          )}
          {buyTokensModalOpen && (
            <BuyTokensModal
              isOpen
              onClose={() => setBuyTokensModalOpen(false)}
              analyticsDataRenderReason='custom_scenario_avatar_generation'
            />
          )}
          {showContinueModal == undefined ? null : (
            <NiceConfirm
              canClose={false}
              heading={'Continue editing?'}
              subHeading={
                <div>
                  <p className="leading-5">
                    You've stopped at step {showContinueModal.step}. Would you like to continue
                    editing or create a new scenario?
                  </p>
                  <p className="pt-3 text-sm leading-4 text-gray-400">
                    If you decide to create a new scenario your progress will be lost.
                  </p>
                </div>
              }
              confirmText={'Continue'}
              cancelText={'Create new'}
              onCancel={() => {
                ls.remove('scenario_wizard_data');
                ls.remove('scenario_wizard_step');
                ls.remove('scenario_wizard_pictures_generated');
                setCurrentStep(1);
                setShowContinueModal(undefined);
              }}
              onConfirm={() => {
                setDataFromStorage();
                setCurrentStep(ls.get('scenario_wizard_step'));
                setPicturesGenerated(ls.get('scenario_wizard_pictures_generated'));
                setShowContinueModal(undefined);
              }}
            />
          )}
        </Wrapper>
      </OuterWrapper>
    </NiceModal>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 652px;

  @media (max-width: 720px) {
    max-width: calc(100vw - 32px - 48px);
  }

  @media (max-width: 400px) {
    max-width: calc(100vw - 24px - 32px);
  }
`;

const Header = styled.span`
  font-family: Raleway;
  font-size: 20px;
  font-weight: 700;
  line-height: 22px;
  padding-bottom: 12px;
`;



const OuterWrapper = styled.div`
  max-width: 700px;
  width: 100%;
  padding: 24px;

  @media (max-width: 720px) {
    max-width: calc(100vw - 32px);
    padding-bottom: 70px;
    padding-top: 50px;
  }

  @media (max-width: 400px) {
    max-width: calc(100vw - 24px);
    padding: 16px;
    padding-bottom: 70px;
    padding-top: 50px;
  }
`;
